'use client';

import { c as _c } from "react/compiler-runtime";
import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useNavigationProgress } from './navigationProgressContext';

// Defined via Emotion's `keyframes` helper rather than inlined in the
// `css` prop. Chakra v3's css processor recurses into string values
// when sibling keys carry `%`-suffixed strings (e.g. `width: '100%'`),
// which crashes with "Cannot create property '@keyframes …' on string
// '100%'". Hoisting the animation here sidesteps that entirely.
const slide = keyframes({
  '0%': {
    left: '-30%',
    width: '30%'
  },
  '50%': {
    left: '40%',
    width: '40%'
  },
  '100%': {
    left: '100%',
    width: '30%'
  }
});

// Thin indeterminate progress bar that sits just below the AppHeader
// while any sidebar Link is in its pending state. The 120ms appear
// delay keeps the bar from flashing on near-instant route changes.
//
// Positioned absolutely so the bar overlays the top of the body area
// instead of consuming flow height. Keeping it in normal flow shrinks
// the digraph canvas by 2px, which shifts every node's y-coordinate
// by ~1px and breaks Playwright `boundingBox` assertions (see the
// "Layout uses height: 100%" gotcha in AGENTS.md).
const NavigationProgressBar = () => {
  const $ = _c(4);
  const {
    active
  } = useNavigationProgress();
  const t0 = !active;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      position: "absolute",
      top: "48px",
      left: 0,
      right: 0,
      height: "2px",
      overflow: "hidden",
      pointerEvents: "none",
      opacity: 0,
      zIndex: 1,
      transition: "opacity 0.15s ease",
      "&[data-active=\"true\"]": {
        opacity: 1,
        transitionDelay: "0.12s"
      },
      "&::after": {
        content: "\"\"",
        position: "absolute",
        top: 0,
        left: "-30%",
        width: "30%",
        height: "100%",
        backgroundColor: "var(--chakra-colors-blue-500)",
        animation: `${slide} 1.1s infinite ease-in-out`
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = active || undefined;
  let t3;
  if ($[1] !== t0 || $[2] !== t2) {
    t3 = <Box aria-hidden={t0} bg="transparent" css={t1} data-active={t2} data-testid="navigation-progress-bar" />;
    $[1] = t0;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
export default NavigationProgressBar;